/**
 * Procedural sound effects via the Web Audio API — no asset files. Synthesizes
 * shoot / impact / explosion / pickup / hit on the fly from oscillators + noise.
 * Init must be called from a user gesture (lobby click) to satisfy autoplay.
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private ambient: { stop: () => void } | null = null;

  init(): void {
    if (this.ctx) {
      if (this.ctx.state === "suspended") void this.ctx.resume();
      return;
    }
    const Ctx: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.45;
    this.master.connect(this.ctx.destination);
    // Pre-bake a 1s white-noise buffer for percussive SFX.
    const len = Math.floor(this.ctx.sampleRate * 1);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuf = buf;
  }

  /** Live-adjust the master output gain (driven by the settings slider). */
  setMasterVolume(v: number): void {
    if (this.master) this.master.gain.value = Math.max(0, Math.min(1, v));
  }

  /**
   * Continuous battlefield atmosphere bed: filtered noise "wind" with a slow
   * LFO drifting the filter frequency (so it doesn't sit static/droney), plus
   * a barely-audible low sine for tension. All procedural, started once and
   * left running for the match — no-op if already running or ctx isn't ready.
   */
  startAmbient(): void {
    if (!this.ctx || !this.master || !this.noiseBuf || this.ambient) return;
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 500;
    filter.Q.value = 0.6;
    const windGain = ctx.createGain();
    windGain.gain.value = 0.05;
    src.connect(filter).connect(windGain).connect(this.master);
    src.start();

    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain).connect(filter.frequency);
    lfo.start();

    const drone = ctx.createOscillator();
    drone.type = "sine";
    drone.frequency.value = 55;
    const droneGain = ctx.createGain();
    droneGain.gain.value = 0.025;
    drone.connect(droneGain).connect(this.master);
    drone.start();

    this.ambient = {
      stop: () => {
        src.stop();
        lfo.stop();
        drone.stop();
        src.disconnect();
        filter.disconnect();
        windGain.disconnect();
        lfo.disconnect();
        lfoGain.disconnect();
        drone.disconnect();
        droneGain.disconnect();
      },
    };
  }

  stopAmbient(): void {
    this.ambient?.stop();
    this.ambient = null;
  }

  private get t(): number {
    return this.ctx ? this.ctx.currentTime : 0;
  }

  private tone(freq: number, dur: number, type: OscillatorType, gain: number, slideTo?: number): void {
    if (!this.ctx || !this.master) return;
    const t = this.t;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), t + dur);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noise(dur: number, filter: BiquadFilterType, freq: number, gain: number): void {
    if (!this.ctx || !this.master || !this.noiseBuf) return;
    const t = this.t;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = this.ctx.createBiquadFilter();
    f.type = filter;
    f.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  shoot(): void {
    this.noise(0.12, "lowpass", 1400, 0.5);
    this.tone(440, 0.12, "square", 0.16, 90);
  }

  impact(): void {
    this.noise(0.08, "bandpass", 2600, 0.3);
    this.tone(320, 0.06, "square", 0.08, 120);
  }

  explosion(): void {
    this.noise(0.5, "lowpass", 480, 0.6);
    this.tone(120, 0.5, "sine", 0.4, 38);
  }

  pickup(): void {
    this.tone(660, 0.12, "sine", 0.28, 1320);
  }

  hit(): void {
    this.tone(95, 0.14, "sine", 0.32, 48);
    this.noise(0.1, "lowpass", 320, 0.18);
  }

  // ── Class ability SFX ──────────────────────────────────────────────

  /** Repair: bright rising chime (healing warmth). */
  repair(): void {
    this.tone(660, 0.14, "sine", 0.26, 990);
    this.tone(990, 0.18, "sine", 0.18, 1320);
  }

  /** Bulwark: heavy metallic clang (shield deploying). */
  bulwark(): void {
    this.noise(0.16, "lowpass", 1200, 0.32);
    this.tone(180, 0.22, "square", 0.22, 120);
  }

  /** Dash: short airy whoosh (blink). */
  dash(): void {
    this.noise(0.16, "highpass", 600, 0.24);
    this.tone(520, 0.16, "sine", 0.14, 180);
  }

  /** Mortar launch: deep lobbed thump. */
  mortarLaunch(): void {
    this.tone(150, 0.25, "sine", 0.38, 60);
    this.noise(0.2, "lowpass", 300, 0.26);
  }

  /** Mortar impact: bigger, longer boom than a normal explosion + a crack. */
  mortarImpact(): void {
    this.noise(0.7, "lowpass", 420, 0.7);
    this.tone(100, 0.6, "sine", 0.45, 30);
    this.noise(0.12, "highpass", 2000, 0.25);
  }
}
